import React from 'react';
import Card from '../../ui/Card';
import Badge from '../../ui/Badge';
import { LocationIcon, CalendarIcon } from '../../ui/Icons';
import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import planigdata from '../../../data/planigdata.json';

const parseMonth = (dateStr) => {
  const lower = dateStr.toLowerCase();
  if (lower.includes('janv')) return { month: 'Janvier', order: 1 };
  if (lower.includes('fév') || lower.includes('fev')) return { month: 'Février', order: 2 };
  if (lower.includes('mars')) return { month: 'Mars', order: 3 };
  if (lower.includes('avril')) return { month: 'Avril', order: 4 };
  if (lower.includes('mai')) return { month: 'Mai', order: 5 };
  if (lower.includes('juin')) return { month: 'Juin', order: 6 };
  if (lower.includes('juil')) return { month: 'Juillet', order: 7 };
  if (lower.includes('août') || lower.includes('aout')) return { month: 'Août', order: 8 };
  if (lower.includes('sept')) return { month: 'Septembre', order: 9 };
  if (lower.includes('oct')) return { month: 'Octobre', order: 10 };
  if (lower.includes('nov')) return { month: 'Novembre', order: 11 };
  if (lower.includes('déc') || lower.includes('dec')) return { month: 'Décembre', order: 12 };
  if (lower.includes('vacances')) return { month: 'Vacances', order: 13 };
  return { month: 'À définir', order: 14 };
};

const extractDayInfo = (dateStr) => {
  const match = dateStr.match(/\b(0?[1-9]|[12][0-9]|3[01])\b/);
  return match ? match[1] : '--';
};

const shortMonth = (month) => {
  const map = {
    'Janvier': 'JAN',
    'Février': 'FÉV',
    'Mars': 'MAR',
    'Avril': 'AVR',
    'Mai': 'MAI',
    'Juin': 'JUI',
    'Juillet': 'JUI',
    'Août': 'AOÛ',
    'Septembre': 'SEP',
    'Octobre': 'OCT',
    'Novembre': 'NOV',
    'Décembre': 'DÉC',
    'Vacances': 'ÉTÉ',
    'À définir': 'TBD'
  };
  return map[month] || month.substring(0, 3).toUpperCase();
};

const isDone = (status) => {
  if (!status) return false;
  const s = status.trim().toLowerCase();
  return s === 'réalisé' || s === 'realise' || s === 'terminé' || s === 'termine';
};

export default function TimelineCalendar({ onlyUpcoming = false }) {
  const eventsData = onlyUpcoming
    ? planigdata.filter(item => !isDone(item.commentaires))
    : planigdata;

  const groupedEvents = {};
  eventsData.forEach(item => {
    const { month, order } = parseMonth(item.datesPeriode);
    if (!groupedEvents[month]) groupedEvents[month] = { order, items: [] };
    groupedEvents[month].items.push(item);
  });

  const sortedMonths = Object.keys(groupedEvents).sort(
    (a, b) => groupedEvents[a].order - groupedEvents[b].order
  );

  return (
    <div className="relative max-w-4xl mx-auto py-8 px-4">
      {/* Central timeline spine line */}
      <div className="absolute top-0 bottom-0 left-8 md:left-1/2 -translate-x-1/2 w-1 bg-gradient-to-b from-emerald-500 via-blue-500 to-indigo-500 rounded-full opacity-30 pointer-events-none" />

      {sortedMonths.map((month, monthIndex) => (
        <div key={monthIndex} className="relative z-10 mb-12">
          {/* Month Header Badge */}
          <div className="flex justify-center mb-8">
            <span className="bg-white/95 backdrop-blur-md border border-acem-border shadow-acem-md px-6 py-2 rounded-full font-title text-lg font-extrabold text-acem-primary-dark tracking-wider uppercase">
              {month}
            </span>
          </div>

          {/* Events list */}
          <div className="space-y-8">
            {groupedEvents[month].items.map((item, index) => {
              const isRealise = isDone(item.commentaires);
              const isLeft = index % 2 === 0;
              const day = extractDayInfo(item.datesPeriode);
              const monthAbbr = shortMonth(month);

              return (
                <div
                  key={index}
                  className={`relative flex flex-col md:flex-row items-center gap-6 ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Timeline central dot */}
                  <div
                    className={`absolute left-8 md:left-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-4 shadow-sm z-20 transition-transform duration-300 hover:scale-125 ${
                      isRealise
                        ? 'bg-emerald-500 border-white ring-4 ring-emerald-100'
                        : 'bg-blue-500 border-white ring-4 ring-blue-100'
                    }`}
                  />

                  {/* Event Card (pl-16 on mobile to offset from left timeline) */}
                  <div className="w-full md:w-1/2 pl-16 md:pl-0 md:px-8">
                    <Card
                      variant="elevated"
                      className={`p-5 flex flex-col sm:flex-row gap-4 transition-all duration-300 hover:-translate-y-1 ${
                        isRealise
                          ? 'bg-gradient-to-br from-emerald-50/70 to-white border-emerald-200/80'
                          : 'bg-white'
                      }`}
                    >
                      {/* Leaf date display */}
                      <div className="flex sm:flex-col items-center justify-between sm:justify-start w-full sm:w-20 rounded-xl overflow-hidden border border-acem-border bg-white shadow-sm flex-shrink-0">
                        <div
                          className={`w-full py-1 text-center text-xs font-bold text-white tracking-wider ${
                            isRealise ? 'bg-emerald-600' : 'bg-red-500'
                          }`}
                        >
                          {monthAbbr}
                        </div>
                        <div className="py-2 px-3 sm:px-0 text-center font-title text-2xl font-extrabold text-acem-text">
                          {day}
                        </div>
                        <span className="hidden sm:block text-[10px] uppercase font-bold text-acem-muted pb-1">
                          Jour
                        </span>
                      </div>

                      {/* Details */}
                      <div className="flex-1 space-y-2">
                        <h3 className={`font-title text-base sm:text-lg font-bold leading-snug ${
                          isRealise ? 'text-emerald-950' : 'text-acem-text'
                        }`}>
                          {item.intitule}
                        </h3>

                        <div className="space-y-1 text-xs sm:text-sm text-acem-muted">
                          <div className="flex items-center gap-1.5">
                            <CalendarIcon className="w-4 h-4 text-acem-primary flex-shrink-0" />
                            <span>{item.datesPeriode}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <LocationIcon className="w-4 h-4 text-acem-primary flex-shrink-0" />
                            <span>{item.typesRencontres}</span>
                          </div>
                        </div>

                        {/* Status */}
                        <div className="pt-2">
                          {isRealise ? (
                            <Badge variant="success">
                              <CheckCircleIcon className="w-3.5 h-3.5 mr-1 inline-block" aria-hidden="true" />
                              Terminé
                            </Badge>
                          ) : (
                            item.commentaires &&
                            item.commentaires !== "//" &&
                            item.commentaires.trim() !== "" && (
                              <p className="text-xs italic text-acem-muted border-t border-dashed border-acem-border pt-1.5 flex items-center gap-1">
                                <InformationCircleIcon className="w-3.5 h-3.5 text-acem-primary flex-shrink-0" aria-hidden="true" />
                                <span>{item.commentaires}</span>
                              </p>
                            )
                          )}
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
